import { DriverMetricsResponse } from "@/graphql/types/graphql";
import { Segment, SegmentationMethod } from "../types/types";
import { DriverSegmentRange, DriverTableSegment } from "../utils/driverTableUtils";

interface UseDriverTableSegmentsProps {
  segmentGroups: { index: number; type: string; method: SegmentationMethod; segments: Segment[] }[];
  data: DriverMetricsResponse[] | undefined;
}

interface DriverTableSegments {
  segments: DriverTableSegment[];
  hasOneDataAndOneManualSegment: boolean;
  isManualOnly: boolean;
}

export const useDriverTableSegments = ({ segmentGroups, data }: UseDriverTableSegmentsProps): DriverTableSegments => {
  const sortedSegmentGroups = [...segmentGroups].sort((a, b) => a.type.localeCompare(b.type));

  const segments: DriverTableSegment[] = sortedSegmentGroups
    .map((group, groupIndex) => {
      if (!group.type) return null;

      if (groupIndex === 1 && sortedSegmentGroups.length === 2) {
        const primaryGroup = sortedSegmentGroups[0];
        const seenRanges = new Set<string>();
        return {
          index: group.index,
          segmentType: group.type,
          method: group.method,
          ranges:
            data
              ?.filter((d) => d.segment === primaryGroup.type && d.segmentRange && !seenRanges.has(d.segmentRange))
              .map((d) => {
                seenRanges.add(d.segmentRange!);
                return {
                  range: group.method === "DATA" ? "" : d.segmentRange || "",
                  segmentValue: d.segmentRange || "",
                  segment: null as Segment,
                  data: null as DriverMetricsResponse,
                  segmentIndex: group.index,
                };
              }) || [],
        };
      }

      const isDataMethod = group.method === "DATA";
      const seenValues = new Set<string>();
      const ranges: DriverSegmentRange[] = isDataMethod
        ? (data?.filter((d) => d.segment === group.type) || []).reduce<DriverSegmentRange[]>((acc, d) => {
            const value = d.segmentValue;
            if (!seenValues.has(value)) {
              seenValues.add(value);
              acc.push({
                range: "",
                segmentValue: d.segmentValue,
                segment: null,
                data: d,
                segmentIndex: group.index,
              });
            }
            return acc;
          }, [])
        : [
            ...group.segments.map((segment) => {
              const range = `${segment.minValue}-${segment.maxValue}`;
              const matchingData = data?.find(
                (d) =>
                  d.segment === group.type && d.segmentValue === range && (groupIndex === 0 || d.segmentRange === range)
              );
              return {
                range,
                segmentValue: range,
                segment,
                data: matchingData,
                segmentIndex: group.index,
              };
            }),
            {
              range: `${group.segments[group.segments.length - 1]?.maxValue}`,
              segmentValue: `${group.segments[group.segments.length - 1]?.maxValue}`,
              segment: null,
              data: data?.find(
                (d) =>
                  d.segment === group.type &&
                  d.segmentValue === `${group.segments[group.segments.length - 1]?.maxValue}` &&
                  (groupIndex === 0 || d.segmentRange === `${group.segments[group.segments.length - 1]?.maxValue}`)
              ),
              segmentIndex: group.index,
            },
          ];

      return {
        index: group.index,
        segmentType: group.type,
        method: group.method,
        ranges,
      };
    })
    .filter((segment): segment is DriverTableSegment => segment !== null);

  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");

  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");

  return { segments, hasOneDataAndOneManualSegment, isManualOnly };
};
